import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { PaperPlaneTilt, Plus, Clock, Check, Warning, Paperclip, Microphone, Stop, Image, File as FileIcon, Play, Pause } from '@phosphor-icons/react';
import { Md } from '@/components/Md';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

function AudioPlayer({ url }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = () => {
    if (!audioRef.current) { audioRef.current = new Audio(`${API}/api/files/${url}`); audioRef.current.onended = () => setPlaying(false); }
    if (playing) { audioRef.current.pause(); setPlaying(false); } else { audioRef.current.play(); setPlaying(true); }
  };
  return <button onClick={toggle} className="flex items-center gap-1.5 text-xs text-brand-green">{playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />} <span>Mensagem de voz</span></button>;
}

function AttachmentPreview({ att }) {
  if (att.type === 'image') return <img src={`${API}/api/files/${att.url}`} alt="" className="max-w-[200px] rounded-xl mt-1 cursor-pointer" onClick={() => window.open(`${API}/api/files/${att.url}`, '_blank')} />;
  if (att.type === 'audio') return <AudioPlayer url={att.url} />;
  return <a href={`${API}/api/files/${att.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-brand-green mt-1"><FileIcon size={12} /> {att.filename}</a>;
}

export default function MensagensClientPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('chat');
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState({});
  const [requests, setRequests] = useState([]);
  const [reqUsage, setReqUsage] = useState({});
  const [categories, setCategories] = useState([]);
  const [showNewReq, setShowNewReq] = useState(false);
  const [reqForm, setReqForm] = useState({ category_id: '', subject: '', description: '', priority: 'normal' });
  const [selectedReq, setSelectedReq] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const messagesEndRef = useRef(null);

  const fetchChat = useCallback(async () => {
    try {
      const { data: c } = await axios.get(`${API}/api/my-conversation`);
      setConv(c);
      const { data: msgs } = await axios.get(`${API}/api/conversations/${c.id}/messages`);
      setMessages(msgs);
      const { data: u } = await axios.get(`${API}/api/conversations/usage/${user.user_id}`);
      setUsage(u || {});
      if (c.unread_by_patient > 0) await axios.put(`${API}/api/conversations/${c.id}/mark-read`);
    } catch (e) {
      // PROMPT 1 fix: erro detalhado em vez de silent catch (era a fonte do "algo correu mal")
      console.error('fetchChat failed', e?.response?.status, e?.response?.data || e?.message);
      toast.error(e?.response?.data?.detail || `Erro a carregar mensagens (${e?.response?.status || 'rede'})`);
    }
  }, [user]);
  const fetchRequests = useCallback(async () => {
    try {
      const [rRes, uRes, cRes] = await Promise.all([
        axios.get(`${API}/api/structured-requests`),
        axios.get(`${API}/api/structured-requests/usage/${user.user_id}`),
        axios.get(`${API}/api/admin/request-categories`),
      ]);
      setRequests(rRes.data); setReqUsage(uRes.data); setCategories(cRes.data);
    } catch (e) {
      // PROMPT 1 fix: erro detalhado em vez de silent catch
      console.error('fetchRequests failed', e?.response?.status, e?.response?.data || e?.message);
      toast.error(e?.response?.data?.detail || `Erro a carregar pedidos (${e?.response?.status || 'rede'})`);
    }
  }, [user]);
  useEffect(() => { fetchChat(); fetchRequests(); }, [fetchChat, fetchRequests]);
  useEffect(() => { if (tab === 'chat') { const i = setInterval(fetchChat, 30000); return () => clearInterval(i); } }, [tab, fetchChat]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const uploadAndSend = async (file) => {
    if (!conv) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: att } = await axios.post(`${API}/api/chat-upload`, fd);
      await axios.post(`${API}/api/conversations/${conv.id}/messages`, { content: '', attachments: [att] });
      fetchChat();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar ficheiro'); }
    finally { setUploading(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new window.File([blob], `voz_${Date.now()}.webm`, { type: 'audio/webm' });
        await uploadAndSend(file);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch { toast.error('Sem acesso ao microfone'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) { mediaRecorderRef.current.stop(); setRecording(false); }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !conv) return;
    setSending(true);
    try { await axios.post(`${API}/api/conversations/${conv.id}/messages`, { content: newMsg }); setNewMsg(''); fetchChat(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setSending(false); }
  };
  const submitRequest = async () => {
    try { await axios.post(`${API}/api/structured-requests`, reqForm); toast.success('Pedido enviado'); setShowNewReq(false); setReqForm({ category_id: '', subject: '', description: '', priority: 'normal' }); fetchRequests(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const limitReached = usage.limit && usage.used >= usage.limit;
  const reqLimitReached = reqUsage.limit && reqUsage.used >= reqUsage.limit;
  const STATUS_STYLES = { aberto: 'bg-brand-gold/20 text-brand-gold-hover', em_resposta: 'bg-brand-mint/20 text-brand-green', resolvido: 'bg-brand-bg text-brand-muted' };

  return (
    <div className="fade-in h-full flex flex-col" data-testid="mensagens-client-page">
      <div className="flex gap-2 mb-3">
        <button onClick={() => setTab('chat')} className={`flex-1 py-2 rounded-xl text-sm font-medium ${tab === 'chat' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Chat</button>
        <button onClick={() => setTab('pedidos')} className={`flex-1 py-2 rounded-xl text-sm font-medium ${tab === 'pedidos' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Pedidos</button>
      </div>
      {tab === 'chat' && (
        <div className="flex-1 flex flex-col min-h-0">
          <p className="text-[10px] text-brand-muted text-center mb-2">Mensagens este mês: {usage.used || 0}/{usage.limit || 'Ilimitado'}</p>
          <div className="flex-1 overflow-y-auto space-y-2 mb-3 bg-brand-bg/30 rounded-2xl p-3" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {messages.length === 0 && <p className="text-center text-brand-muted text-sm py-8">Começa uma conversa com a tua nutricionista</p>}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.sender_type === 'patient' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${m.sender_type === 'patient' ? 'bg-brand-mint/40 text-brand-text' : 'bg-white border border-brand-border text-brand-text'}`}>
                  {m.content && <Md as="p" className="text-sm">{m.content}</Md>}
                  {(m.attachments || []).map((att, ai) => <AttachmentPreview key={ai} att={att} />)}
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[8px] text-brand-muted">{m.created_at?.slice(11, 16)}</span>
                    {m.sender_type === 'patient' && m.read_at && <Check size={10} className="text-brand-green" />}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {limitReached ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 text-center"><Warning size={14} className="inline mr-1" />Limite mensal atingido. Repõe dia 1.</div>
          ) : (
            <div className="flex gap-2 items-end">
              <label className="p-2.5 text-brand-muted hover:text-brand-green cursor-pointer rounded-xl hover:bg-brand-bg" data-testid="attach-btn">
                <Paperclip size={18} />
                <input type="file" accept="image/*,.pdf,.doc,.docx,.xlsx" onChange={e => e.target.files?.[0] && uploadAndSend(e.target.files[0])} className="hidden" disabled={uploading} />
              </label>
              {recording ? (
                <button onClick={stopRecording} className="p-2.5 bg-red-500 text-white rounded-xl animate-pulse" data-testid="stop-record-btn"><Stop size={18} weight="fill" /></button>
              ) : (
                <button onClick={startRecording} className="p-2.5 text-brand-muted hover:text-brand-green rounded-xl hover:bg-brand-bg" data-testid="record-btn"><Microphone size={18} /></button>
              )}
              <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Escreve uma mensagem..." className="flex-1 rounded-xl border-brand-border" data-testid="chat-input" disabled={uploading} />
              <button onClick={sendMessage} disabled={sending || uploading || !newMsg.trim()} className="bg-brand-green text-white rounded-xl px-4 py-2.5 disabled:opacity-40" data-testid="send-msg-btn"><PaperPlaneTilt size={18} /></button>
            </div>
          )}
          {uploading && <p className="text-[10px] text-brand-muted text-center mt-1">A enviar ficheiro...</p>}
        </div>
      )}
      {tab === 'pedidos' && (
        <div className="flex-1">
          <p className="text-[10px] text-brand-muted text-center mb-2">Pedidos este mês: {reqUsage.used || 0}/{reqUsage.limit || 'Ilimitado'}</p>
          {requests.length === 0 ? <p className="text-center text-brand-muted py-8 text-sm">Sem pedidos.</p> : (
            <div className="space-y-2 mb-3">
              {requests.map(r => (
                <button key={r.id} onClick={() => setSelectedReq(r)} className="w-full text-left bg-white border border-brand-border rounded-xl p-3 hover:border-brand-green/30">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] || ''}`}>{r.status}</span>
                    {r.priority === 'urgente' && <span className="text-[8px] text-red-500 font-medium">URGENTE</span>}
                  </div>
                  <p className="text-sm font-medium text-brand-text">{r.subject}</p>
                  <p className="text-[10px] text-brand-muted">{r.created_at?.split('T')[0]}</p>
                </button>
              ))}
            </div>
          )}
          {!reqLimitReached && <button onClick={() => setShowNewReq(true)} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="new-request-btn"><Plus size={14} className="inline mr-1" /> Novo pedido</button>}
          <Dialog open={showNewReq} onOpenChange={setShowNewReq}>
            <DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">Novo pedido</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <select value={reqForm.category_id} onChange={e => setReqForm({ ...reqForm, category_id: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white"><option value="">Categoria...</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
                <Input value={reqForm.subject} onChange={e => setReqForm({ ...reqForm, subject: e.target.value })} placeholder="Assunto (máx. 100 caracteres)" maxLength={100} className="rounded-xl border-brand-border" />
                <Textarea value={reqForm.description} onChange={e => setReqForm({ ...reqForm, description: e.target.value })} placeholder="Descrição do pedido..." rows={4} className="rounded-xl border-brand-border resize-none" />
                <label className="flex items-center gap-2"><input type="checkbox" checked={reqForm.priority === 'urgente'} onChange={e => setReqForm({ ...reqForm, priority: e.target.checked ? 'urgente' : 'normal' })} className="rounded" /><span className="text-xs text-brand-muted">Marcar como urgente</span></label>
                <button onClick={submitRequest} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Enviar pedido</button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={!!selectedReq} onOpenChange={v => { if (!v) setSelectedReq(null); }}>
            <DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">{selectedReq?.subject}</DialogTitle></DialogHeader>
              {selectedReq && (
                <div className="space-y-3">
                  <p className="text-xs text-brand-muted">{selectedReq.created_at?.split('T')[0]} | <span className={`px-2 py-0.5 rounded-full ${STATUS_STYLES[selectedReq.status]}`}>{selectedReq.status}</span></p>
                  <div className="bg-brand-bg/50 rounded-xl p-3"><Md as="p" className="text-sm text-brand-text">{selectedReq.description}</Md></div>
                  {selectedReq.nutri_response && (
                    <div className="bg-white border border-brand-green/20 rounded-xl p-3"><p className="text-[9px] text-brand-green uppercase mb-1">Resposta da nutricionista</p><Md as="p" className="text-sm text-brand-text">{selectedReq.nutri_response}</Md></div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
